/**
 * 支付报表 / 支付记录展示格式化工具
 */

const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: '$',
  CNY: '¥',
  EUR: '€',
  GBP: '£',
  JPY: '¥',
  HKD: 'HK$',
  AUD: 'A$',
  CAD: 'C$',
  SGD: 'S$',
};

export const CURRENCY_OPTIONS = ['USD', 'CNY', 'EUR', 'GBP', 'HKD', 'JPY'];

export function currencySymbol(currency?: string): string {
  if (!currency) return '';
  return CURRENCY_SYMBOLS[currency.toUpperCase()] || '';
}

/** 金额本身固定 2 位小数，直接拼接币种符号，不参与汇率折算 */
export function formatMoney(amount: string | number | undefined | null, currency?: string): string {
  if (amount === undefined || amount === null || amount === '') return '-';
  const symbol = currencySymbol(currency);
  return `${symbol}${amount}${!symbol && currency ? ` ${currency}` : ''}`;
}

/** 0~1 的比率转百分比 */
export function formatRate(rate: number | undefined | null, digits = 1): string {
  if (rate === undefined || rate === null || Number.isNaN(rate)) return '-';
  return `${(rate * 100).toFixed(digits)}%`;
}

/** 图表 Y 轴用的紧凑金额，如 $1.2k */
export function formatCompact(value: number, currency?: string): string {
  const symbol = currencySymbol(currency);
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return `${symbol}${(value / 1_000_000).toFixed(1)}m`;
  if (abs >= 1_000) return `${symbol}${(value / 1_000).toFixed(1)}k`;
  if (abs >= 100) return `${symbol}${value.toFixed(0)}`;
  return `${symbol}${value.toFixed(2)}`;
}

export const PAY_STATUS_META: Record<number, { label: string; className: string }> = {
  0: { label: 'Pending', className: 'text-amber-400 bg-amber-400/10 border-amber-400/20' },
  1: { label: 'Paid', className: 'text-emerald-400 bg-emerald-400/10 border-emerald-400/20' },
  2: { label: 'Refunded', className: 'text-zinc-300 bg-zinc-400/10 border-zinc-400/20' },
  3: { label: 'Closed', className: 'text-red-400 bg-red-400/10 border-red-400/20' },
};

export const NOTIFY_STATUS_META: Record<number, { label: string; className: string }> = {
  0: { label: 'Not sent', className: 'text-zinc-400 bg-zinc-400/10 border-zinc-400/20' },
  1: { label: 'Delivered', className: 'text-emerald-400 bg-emerald-400/10 border-emerald-400/20' },
  2: { label: 'Failed', className: 'text-red-400 bg-red-400/10 border-red-400/20' },
};

export function payStatusMeta(status: number) {
  return PAY_STATUS_META[status] || {
    label: `Status ${status}`,
    className: 'text-zinc-400 bg-zinc-400/10 border-zinc-400/20',
  };
}

export function notifyStatusMeta(status: number) {
  return NOTIFY_STATUS_META[status] || {
    label: `Notify ${status}`,
    className: 'text-zinc-400 bg-zinc-400/10 border-zinc-400/20',
  };
}

/** YYYY-MM-DD（本地时间） */
export function toDateString(date: Date): string {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** 今天往前推 days 天 */
export function daysAgo(days: number): string {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return toDateString(date);
}

export function today(): string {
  return toDateString(new Date());
}

/** 两个 YYYY-MM-DD 之间的自然日跨度（含首尾） */
export function rangeDays(start: string, end: string): number {
  const startDate = new Date(`${start}T00:00:00`);
  const endDate = new Date(`${end}T00:00:00`);
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) return 0;
  const diff = Math.round((endDate.getTime() - startDate.getTime()) / 86_400_000);
  return diff + 1;
}

export function formatTimestamp(timestamp?: number): string {
  if (!timestamp) return '-';
  return new Date(timestamp * 1000).toLocaleString();
}
